import React, { useState, useMemo, useEffect } from 'react';
import {
  Search,
  MoreVertical,
  UserPlus,
  Shield,
  DollarSign,
  MessageSquare,
  Trophy,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatsCard } from '@/components/shared/StatsCard';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '@/authContext/AuthContext';
import {
  getUserDashboard,
  getUserById,
  getAllRoles,
  deleteUser,
  suspendUser,
  activateUser,
  changeUserRole,
  updateUserEmail,
} from '@/services/user';

export const UsersPage = () => {
  const { users, usersLoading, fetchUsers } = useAuth();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [editRoleUser, setEditRoleUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const [editEmailUser, setEditEmailUser] = useState(null);
  const [newEmail, setNewEmail] = useState('');

  const [deleteItem, setDeleteItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Dashboard stats from API
  const [stats, setStats] = useState({
    total_users: 0,
    new_users_this_week: 0,
    active_users: 0,
    suspended_users: 0,
  });

  useEffect(() => {
    fetchUsers();
    loadDashboard();
    loadRoles();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await getUserDashboard();
      setStats({
        total_users: res?.total_users ?? 0,
        new_users_this_week: res?.new_users_this_week ?? 0,
        active_users: res?.active_users ?? 0,
        suspended_users: res?.suspended_users ?? 0,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const loadRoles = async () => {
    try {
      const res = await getAllRoles();
      setRoles(res?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const filteredUsers = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter(
      (u) =>
        u?.username?.toLowerCase().includes(q) ||
        u?.email?.toLowerCase().includes(q)
    );
  }, [users, searchTerm]);

  // ==================== ACTIONS ====================
  const openProfile = async (user) => {
    setSelectedUser(user);
    try {
      setDetailLoading(true);
      const res = await getUserById(user.id);
      if (res?.data) setSelectedUser(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSuspendOrActivate = async (user) => {
    setActionLoading(true);
    try {
      if (user.status === 'active') {
        await suspendUser(user.id);
        toast.success('User suspended');
      } else {
        await activateUser(user.id);
        toast.success('User activated');
      }
      fetchUsers();
      loadDashboard();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    try {
      await deleteUser(deleteItem.id);
      toast.success('User deleted');
      setDeleteItem(null);
      setSelectedUser(null);
      fetchUsers();
      loadDashboard();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeRole = async () => {
    if (!editRoleUser || !selectedRoleId) {
      toast.error('Please select a role');
      return;
    }
    setActionLoading(true);
    try {
      await changeUserRole({ id: editRoleUser.id, role_id: selectedRoleId });
      toast.success('Role updated');
      setEditRoleUser(null);
      setSelectedRoleId('');
      fetchUsers();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to change role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Please enter a valid email');
      return;
    }
    setActionLoading(true);
    try {
      await updateUserEmail({ id: editEmailUser.id, email: newEmail });
      toast.success('Email updated');
      setEditEmailUser(null);
      fetchUsers();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to update email');
    } finally {
      setActionLoading(false);
    }
  };

  const displayName = (u) => u?.username || u?.name || '—';

  return (
    <div>
      <PageHeader
        title="Users"
        description="Manage user accounts and permissions"
        breadcrumbs={['Admin', 'Community', 'Users']}
      />

      <div className="grid gap-6 md:grid-cols-4 mb-6">
        <StatsCard
          title="Total Users"
          value={stats.total_users}
          icon={UserPlus}
          iconBg="bg-blue-100 text-blue-600"
        />
        <StatsCard
          title="New This Week"
          value={stats.new_users_this_week}
          icon={Trophy}
          iconBg="bg-purple-100 text-purple-600"
        />
        <StatsCard
          title="Active"
          value={stats.active_users}
          icon={Shield}
          iconBg="bg-emerald-100 text-emerald-600"
        />
        <StatsCard
          title="Suspended"
          value={stats.suspended_users}
          icon={Shield}
          iconBg="bg-red-100 text-red-600"
        />
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {usersLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      User
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Role
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                      Reviews
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Joined
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Status
                    </TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-12 text-slate-500"
                      >
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback>
                                {displayName(user)?.[0]?.toUpperCase() || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {displayName(user)}
                              </p>
                              <p className="text-xs text-slate-500">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">
                            {user.role || '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-medium text-slate-900">
                            {user.reviews_count ?? 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {user.created_at
                              ? formatDistanceToNow(new Date(user.created_at), {
                                  addSuffix: true,
                                })
                              : '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div
                              className={`h-2 w-2 rounded-full ${
                                user.status === 'active'
                                  ? 'bg-emerald-500'
                                  : 'bg-red-500'
                              }`}
                            />
                            <span className="text-sm capitalize">{user.status}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openProfile(user)}>
                                View Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setEditEmailUser(user);
                                  setNewEmail(user.email || '');
                                }}
                              >
                                Edit Email
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setEditRoleUser(user);
                                  setSelectedRoleId('');
                                }}
                              >
                                Change Role
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleSuspendOrActivate(user)}
                                disabled={actionLoading}
                              >
                                {user.status === 'active' ? 'Suspend' : 'Activate'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => setDeleteItem(user)}
                              >
                                Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Sheet */}
      <Sheet
        open={!!selectedUser}
        onOpenChange={(open) => {
          if (!open) setSelectedUser(null);
        }}
      >
        <SheetContent className="w-[480px] overflow-y-auto">
          {selectedUser && (
            <>
              <SheetHeader>
                <SheetTitle>User Profile</SheetTitle>
              </SheetHeader>
              {detailLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="mt-6 space-y-6">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-20 w-20">
                      <AvatarFallback className="text-2xl">
                        {displayName(selectedUser)?.[0]?.toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900">
                        {displayName(selectedUser)}
                      </h3>
                      <p className="text-sm text-slate-600">{selectedUser.email}</p>
                      <Badge variant="outline" className="mt-2 capitalize">
                        {selectedUser.role || '—'}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <MessageSquare className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                        <p className="text-2xl font-bold text-slate-900">
                          {selectedUser.review_count ??
                            selectedUser.reviews_count ??
                            0}
                        </p>
                        <p className="text-xs text-slate-600">Reviews</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <DollarSign className="h-6 w-6 mx-auto mb-2 text-emerald-600" />
                        <p className="text-2xl font-bold text-slate-900">
                          {selectedUser.payout_count ?? 0}
                        </p>
                        <p className="text-xs text-slate-600">Payouts</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Shield className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                        <p className="text-sm font-medium text-slate-900 capitalize">
                          {selectedUser.status}
                        </p>
                        <p className="text-xs text-slate-600">Status</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Trophy className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                        <p className="text-sm font-medium text-slate-900">
                          {selectedUser.email_verified ? 'Yes' : 'No'}
                        </p>
                        <p className="text-xs text-slate-600">Email Verified</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">ACCOUNT INFO</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Joined</span>
                        <span className="font-medium">
                          {selectedUser.created_at
                            ? new Date(selectedUser.created_at).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Last Login</span>
                        <span className="font-medium">
                          {selectedUser.last_login_at
                            ? formatDistanceToNow(
                                new Date(selectedUser.last_login_at),
                                { addSuffix: true }
                              )
                            : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Role</span>
                        <span className="font-medium capitalize">
                          {selectedUser.role || '—'}
                        </span>
                      </div>
                      {selectedUser.bio && (
                        <div>
                          <span className="text-slate-600">Bio</span>
                          <p className="font-medium mt-1">{selectedUser.bio}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Change Role */}
      <Dialog
        open={!!editRoleUser}
        onOpenChange={() => setEditRoleUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>User</Label>
              <p className="text-sm font-medium text-slate-900 mt-2">
                {displayName(editRoleUser)}
              </p>
            </div>
            <div>
              <Label>New Role</Label>
              <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-full"
              onClick={handleChangeRole}
              disabled={actionLoading}
            >
              {actionLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Update Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Email */}
      <Dialog
        open={!!editEmailUser}
        onOpenChange={() => setEditEmailUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>User</Label>
              <p className="text-sm font-medium text-slate-900 mt-2">
                {displayName(editEmailUser)}
              </p>
            </div>
            <div>
              <Label>Current Email</Label>
              <p className="text-sm text-slate-600 mt-2">{editEmailUser?.email}</p>
            </div>
            <div>
              <Label>New Email Address</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email"
                className="mt-2"
              />
            </div>
            <Button
              className="w-full"
              onClick={handleUpdateEmail}
              disabled={actionLoading}
            >
              {actionLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Update Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteItem}
        onOpenChange={() => setDeleteItem(null)}
        title="Delete User"
        description={`Are you sure you want to delete "${displayName(
          deleteItem
        )}"? This action cannot be undone.`}
        confirmLabel="Delete User"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};